import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import { Icon, type IconName } from './Icon';

type Variant = 'primary' | 'ok' | 'start' | 'line' | 'danger' | 'ghost' | 'link';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  icon?: IconName;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'line',
  size = 'md',
  block,
  icon,
  className,
  children,
  type,
  ...rest
}: ButtonProps) {
  const cls = ['btn', `btn--${variant}`, size !== 'md' && `btn--${size}`, block && 'btn--block', className]
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type ?? 'button'} className={cls} {...rest}>
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  bare,
  className,
  ...rest
}: { icon: IconName; label: string; bare?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={['icon-btn', bare && 'icon-btn--bare', className].filter(Boolean).join(' ')}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}

/**
 * Two-tap confirmation for bulk / destructive actions (PRD: no pop-ups except bulk
 * actions). The first tap arms the button and changes its label; a second tap within
 * `timeoutMs` confirms. It disarms itself after the timeout.
 */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  timeoutMs = 4000,
  armedVariant = 'ok',
  variant = 'line',
  ...rest
}: Omit<ButtonProps, 'onClick' | 'children'> & {
  label: ReactNode;
  confirmLabel: ReactNode;
  onConfirm: () => void;
  timeoutMs?: number;
  armedVariant?: Variant;
}) {
  const [armed, setArmed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <Button
      {...rest}
      variant={armed ? armedVariant : variant}
      className={[rest.className, armed && 'btn--armed'].filter(Boolean).join(' ')}
      aria-live="polite"
      onClick={() => {
        if (armed) {
          clearTimeout(timer.current);
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
          timer.current = setTimeout(() => setArmed(false), timeoutMs);
        }
      }}
    >
      {armed ? confirmLabel : label}
    </Button>
  );
}
